import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles, Loader2, ExternalLink, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { aiCreditsService, AiCreditPack, AiCreditSummary, reais, creditos } from '../../services/aiCreditsService';

interface Props {
  open: boolean;
  onClose: () => void;
  summary: AiCreditSummary | null;
  onRefresh: () => Promise<void>;
}

/**
 * Recarga de créditos de IA sem sair da tela: o pagamento abre numa aba nova (o Stripe não roda
 * dentro da janela), a tela atual fica como está e o saldo atualiza quando o pagamento entra.
 */
const AiRechargeModal: React.FC<Props> = ({ open, onClose, summary, onRefresh }) => {
  const { t } = useTranslation();
  const [packs, setPacks] = useState<Record<string, AiCreditPack> | null>(null);
  const [comprando, setComprando] = useState<string | null>(null);
  const [linkPagamento, setLinkPagamento] = useState<string | null>(null);
  const disponivelAntes = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setLinkPagamento(null);
    aiCreditsService.packs().then(setPacks).catch(() => setPacks({}));
  }, [open]);

  // O pagamento entrou (o saldo subiu desde o clique em comprar): avisa e fecha
  useEffect(() => {
    if (!open || disponivelAntes.current == null || !summary) return;
    if (summary.available_cents > disponivelAntes.current) {
      toast.success(t('aiCredits.recharged', 'Créditos de IA adicionados. Pode continuar de onde parou.'));
      disponivelAntes.current = null;
      setComprando(null);
      setLinkPagamento(null);
      onClose();
    }
  }, [summary, open, onClose, t]);

  if (!open) return null;

  const comprar = async (id: string) => {
    // A aba precisa ser aberta no clique (senão o navegador bloqueia); o endereço vem depois
    const aba = window.open('', '_blank');
    setComprando(id);
    disponivelAntes.current = summary?.available_cents ?? 0;
    try {
      const { url } = await aiCreditsService.checkout(id);
      if (aba && !aba.closed) aba.location.href = url;
      // Também como link: se o navegador bloqueou a aba, a pessoa abre por aqui
      setLinkPagamento(url);
    } catch (e: any) {
      if (aba && !aba.closed) aba.close();
      disponivelAntes.current = null;
      setComprando(null);
      toast.error(e?.response?.data?.message || t('aiCredits.checkoutError', 'Não foi possível abrir o pagamento'));
    }
  };

  const lista = packs ? Object.entries(packs) : [];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-900 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('aiCredits.rechargeTitle', 'Recarregar créditos de IA')}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label={t('common.close', 'Fechar')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {summary && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2">
                <p className="text-[11px] text-gray-500 dark:text-gray-400">{t('aiCredits.available', 'Disponível')}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{reais(summary.available_cents)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2">
                <p className="text-[11px] text-gray-500 dark:text-gray-400">{t('aiCredits.planMonth', 'Do plano no mês')}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{reais(summary.included_remaining_cents)} / {reais(summary.monthly_allowance_cents)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-2">
                <p className="text-[11px] text-gray-500 dark:text-gray-400">{t('aiCredits.purchased', 'Comprado')}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{reais(summary.purchased_balance_cents)}</p>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('aiCredits.rechargeHelp', 'Cada uso de IA consome créditos (1 crédito = R$ 0,01). O plano renova a franquia todo mês; o que você compra não vence. O pagamento abre em outra aba e esta tela continua como está.')}
          </p>

          {!packs && <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>}
          <div className="space-y-2">
            {lista.map(([id, p]) => (
              <div key={id} className={`flex items-center justify-between rounded-lg border px-4 py-3 ${p.popular ? 'border-primary-400 dark:border-primary-600' : 'border-gray-200 dark:border-gray-700'}`}>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {p.label} {p.popular && <span className="ml-1 rounded bg-primary-100 dark:bg-primary-900/40 px-1.5 py-0.5 text-[10px] text-primary-700 dark:text-primary-300">{t('aiCredits.popular', 'Mais escolhido')}</span>}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('aiCredits.packCredits', '{{n}} créditos ({{valor}} em uso de IA)', { n: creditos(p.credits), valor: reais(p.credits) })}</p>
                </div>
                <button
                  type="button"
                  disabled={!!comprando}
                  onClick={() => comprar(id)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  {comprando === id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {(p.price_brl).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </button>
              </div>
            ))}
          </div>

          {comprando && (
            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-xs text-blue-800 dark:text-blue-300 space-y-1.5">
              <p>{t('aiCredits.waiting', 'O pagamento abriu em outra aba. Assim que for concluído, o saldo atualiza aqui sozinho.')}</p>
              <div className="flex flex-wrap gap-3">
                {linkPagamento && (
                  <a href={linkPagamento} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium underline">
                    <ExternalLink className="h-3.5 w-3.5" />{t('aiCredits.openPayment', 'Abrir o pagamento')}
                  </a>
                )}
                <button type="button" onClick={() => onRefresh()} className="inline-flex items-center gap-1 font-medium underline">
                  <Check className="h-3.5 w-3.5" />{t('aiCredits.alreadyPaid', 'Já paguei, atualizar saldo')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiRechargeModal;
